import type { WorkspaceRole } from '../enums.js';
import type { AgentConnectState } from '../agent-connect.js';
import type { MemoryCardFile } from '../agent-memory.js';
import type { Actionable } from './actions.js';
import type { AgentCardKind } from './index.js';
import type { OpCardRoute } from './routes.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * E · OPERASYON VE HESAP KARTLARI — alan ajanı E (Faz 8 dalga 2, 30 Eyl 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * On bir tür: `automation_rule`, `tasks`, `activity`, `approvals`, `export`,
 * `balance`, `needs_action`, `connections_overview`, `account_status`, `team`,
 * `memory`. (`leads` ve `policy_issue` 30 Eyl 2026'da D'nin alanına taşındı —
 * `./engagement.ts`.) Web görünümleri `tool-renderers/ops.tsx` (+ `ops-*.tsx`),
 * API kaynakları `apps/api/src/agent/cards/ops/`, araç kartları
 * `app-tools/*.tools.ts` (`card`) ve operatör araçları
 * (`agent-operator-tool.service.ts` → `ui`).
 *
 *   · 🔴 KİŞİSEL VERİ: ekip adı/e-postası, AI notunun METNİ karta YAZILMAZ —
 *     kart kimlik/sayı/durum taşır, görüntü verisi canlı uçtan
 *     (`OpsCardLiveData`). Sunucu kemeri (`finalize.ts`) `email`, `phone`,
 *     `firstName`… anahtarlarını yakalayıp kartı DÜŞÜRÜR.
 *   · 🔴 SÜRELİ BAĞLANTI KARTA YAZILMAZ: dışa aktarma indirme adresi her
 *     çizimde canlı uçtan TAZE (`export`); model sonucunda da yok.
 *   · 🔴 İNSAN KARARI: `approvals` satır içi onay/ret İNSANIN düğmesidir, AI
 *     karar vermez (mevcut karar uçları: `/agent/proposals/:id/decide`,
 *     `/automations/approvals/decide`); `balance` transfer formu, `team`
 *     daveti, `activity` geri alma, `tasks` iptali, `memory` silme de insan
 *     eylemi. `account_status` salt okunur — plan/otonomi ayarı sayfada.
 *   · Metin DEĞİL anahtar: adım, sebep, eylem kodları (`code` + `params`) —
 *     ekran `dashboard.agentChat.cards.<tür>.*` ile çevirir (aşağıdaki
 *     `*_CODES` listeleri iki tarafın ortak sözlüğü).
 */

// ═══════════════════════════════════════════════════════════════════════════
// Ortak sözlükler — sunucu KOD gönderir, ekran çevirir
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `activity` satırının işi — `cards.activity.action.<kod>`. Otomasyon eylem
 * türleri (`ACTION_TYPES`) + öneri araçları + operatör/kart olayları. Listede
 * olmayan kod ekranda genel "değişiklik" metnine düşer (açık liste).
 */
export const ACTIVITY_ACTION_CODES = [
  'pause',
  'resume',
  'budget_adjust',
  'budget_redistribute',
  'notify',
  'notify_external',
  'creative_improve',
  'add_negative_keyword',
  'pause_keyword',
  'adjust_bid',
  'platform_op',
  'platform_op_plan',
  'app_op',
  'create_campaign',
  'plan_campaign',
  'generate_creative',
  'operator_run',
  'card_action',
  'rolled_back',
  'change',
] as const;
export type ActivityActionCode = (typeof ACTIVITY_ACTION_CODES)[number];

/** Geri alınamama sebebi — `cards.activity.noUndo.<kod>`. */
export const ACTIVITY_NO_UNDO_CODES = [
  /** Uygulanmadı (bekliyor/başarısız/reddedildi) — geri alınacak bir şey yok. */
  'not_applied',
  /** Zaten geri alındı. */
  'already_undone',
  /** Önceki değer kaydedilmemiş (bütçe, teklif) — tahminle geri yazılmaz. */
  'no_previous_value',
  /** İşlemin bilinen bir tersi yok (silme, bildirim, oluşturma…). */
  'no_inverse',
  /** Koşu/kayıt satırı — bir değişiklik değil. */
  'not_a_change',
  /** Bu iş türü panelden geri alınıyor (ör. kural ayarı) — bağlantı var. */
  'in_panel',
] as const;
export type ActivityNoUndoCode = (typeof ACTIVITY_NO_UNDO_CODES)[number];

/**
 * `needs_action` adımları — `cards.needs_action.step.<kod>` (parametre:
 * `platform`, `role`, `seconds`). MÜŞTERİNİN yapacağı iş; Mentorivo'nun
 * (geliştiricinin) işi `contact_support` / `feature_not_enabled` /
 * `wait_platform_access` ile dürüstçe söylenir — `.env`, sunucu bayrağı,
 * uygulama incelemesi gibi iç ayrıntı karta GİRMEZ.
 */
export const NEEDS_ACTION_STEP_CODES = [
  'connect_account',
  'reconnect',
  'grant_scope',
  'grant_platform_role',
  'accept_terms',
  'fix_billing',
  'wait_platform_access',
  'feature_not_enabled',
  'contact_support',
  'wait_and_retry',
  'upgrade_plan',
  'ask_admin',
  'remove_demo',
  'check_platform',
] as const;
export type NeedsActionStepCode = (typeof NEEDS_ACTION_STEP_CODES)[number];

export const NEEDS_ACTION_CATEGORIES = [
  'ACCESS_REQUIRED',
  'RECONNECT_REQUIRED',
  'FEATURE_FLAG',
  'ACCOUNT_SETUP',
  'PERMISSION',
  'QUOTA',
  'PLAN',
  'OTHER',
] as const;
export type NeedsActionCategory = (typeof NEEDS_ACTION_CATEGORIES)[number];

/** Takip koşusunun sohbete düşen sonucu — `cards.tasks.result.<kod>`. */
export const TASK_RESULT_OUTCOMES = ['findings', 'noFindings', 'failed', 'skipped'] as const;
export type TaskResultOutcome = (typeof TASK_RESULT_OUTCOMES)[number];

/** Bakiye satırının durumu — `cards.balance.state.<kod>`. */
export const BALANCE_STATES = ['ok', 'low', 'blocked', 'unknown', 'unsupported', 'error'] as const;
export type BalanceState = (typeof BALANCE_STATES)[number];

/** Bakiye notu — `cards.balance.note.<kod>`. */
export const BALANCE_NOTE_CODES = [
  'billing_hold',
  'no_billing_api',
  'account_disabled',
  'read_failed',
  'demo',
  'prepaid',
  'postpaid',
  'invoice',
  'no_budget_limit',
] as const;
export type BalanceNoteCode = (typeof BALANCE_NOTE_CODES)[number];

// ═══════════════════════════════════════════════════════════════════════════
// Kartlar
// ═══════════════════════════════════════════════════════════════════════════

export type OpsAgentCard =
  /**
   * OTOMASYON KURALI — EĞER/O ZAMAN cümlesi + mini akış + son koşu (tek kart).
   * `automation_get` (kural). Deneme çalıştırması kartın içinde istenebilir
   * (canlı uç — hiçbir şey kaydedilmez). Ayrı `automation_dry_run` aracı 5 Eki
   * 2026'da kural yazan araçlarla birlikte kalktı.
   */
  | Actionable<{
      kind: 'automation_rule';
      /** Kayıtlı kural — kaydedilmemiş taslağın denemesinde `null`. */
      workflowId: string | null;
      /** Kullanıcının verdiği kural adı (kişisel veri değil); taslakta `null`. */
      name: string | null;
      enabled: boolean | null;
      /** `SUGGEST` | `AUTOPILOT` */
      mode: string | null;
      /** `WorkflowRow.triggerSummary` — ekran `useTriggerText` ile çevirir. */
      trigger: { key: string; params?: Record<string, string | number> } | null;
      /**
       * Koşul YAPRAKLARI (en fazla 6) — `key: 'rule'`, params
       * `{ metric, operator, value, valueTo?, windowDays }`.
       */
      conditions: { key: string; params?: Record<string, string | number> }[];
      conditionOp: 'AND' | 'OR' | null;
      /** Toplam yaprak — kart 6'dan fazlasını "+N" diye söyler. */
      conditionCount: number;
      /** Kuralın KENDİ eylemleri (kart düğmeleri `actions`ta — karıştırma). */
      ruleActions: { type: string; params?: Record<string, string | number> }[];
      /** Kapsamın şu anki büyüklüğü (kampanya/set sayısı). */
      scopeCount: number | null;
      /** `connection_lost` | `entitlement` | `no_scope` */
      blocked: string | null;
      /** Deneme sonucu (araçtan geldiyse). Hedef ADI kampanya adı — kişisel veri değil. */
      dryRun: {
        days: number;
        triggers: number;
        scopeSize: number;
        hits: {
          date: string;
          targetName: string;
          actions: { summaryKey: string; params: Record<string, string | number> }[];
          skipped: boolean;
        }[];
        /** Kırpılmadan önceki isabet sayısı. */
        total: number;
      } | null;
      lastRuns: { runId: string; status: string; at: string }[];
    }>
  /**
   * OPERATÖR TAKİPLERİ — zaman çizelgesi, iptal. `list_tasks`, `schedule_task`
   * ve (koşu bitince) sohbete düşen takip sonucu (`result`).
   */
  | Actionable<{
      kind: 'tasks';
      /** Arka plan operatörü açık mı — kapalıysa sohbetten kurulan takip BEKLER. */
      operatorEnabled: boolean;
      items: {
        taskId: string;
        /** AI/kullanıcının yazdığı iş başlığı (kişisel veri değil). */
        title: string;
        /** Sistem görevinde çeviri anahtarı (`operator.systemTask.*`). */
        titleKey: string | null;
        titleParams: Record<string, string> | null;
        taskKind: 'FOLLOW_UP' | 'RECURRING' | 'WATCH' | 'PATROL';
        dueAt: string | null;
        intervalMinutes: number | null;
        status: 'scheduled' | 'running' | 'done' | 'failed' | 'canceled';
        createdBy: 'agent' | 'user' | 'system';
        /** Kartı üreten kişinin kurduğu görev mi (sohbet kişiye özel). */
        mine: boolean;
        /** Kurucu yönetici değil → koşu SALT OKUMA/ÖNERİ (yazma yapmaz). */
        readOnly: boolean;
        /** Operatör kapalıyken koşmayacak (sohbetten/AI'dan kurulan görev). */
        waitsForOperator: boolean;
        lastRunAt: string | null;
      }[];
      total: number;
      /** Takip koşusunun sonucu — koşucu kaynak sohbete yazarken dolduruyor. */
      result: {
        runId: string;
        taskId: string | null;
        outcome: TaskResultOutcome;
        readOnly: boolean;
        at: string;
      } | null;
    }>
  /**
   * AI'IN YAPTIKLARI — otopilot + operatör + kural + sohbet kartı, tek zaman
   * çizelgesi (`activity_feed`, `automation_logs`). Ters işlemi BİLİNEN işte
   * "geri al" eylemi (`undo:<id>` — `actions`ta); bilinmeyende `noUndo` kodu.
   */
  | Actionable<{
      kind: 'activity';
      range: { from: string; to: string } | null;
      items: {
        /** `log:<uuid>` | `run:<uuid>` | `audit:<uuid>` */
        id: string;
        at: string;
        origin: 'autopilot' | 'operator' | 'agent' | 'user' | 'automation';
        /** `cards.activity.action.<kod>` — `ACTIVITY_ACTION_CODES`. */
        action: string;
        /** Katalog işleminin başlığı (katalog iki dilli). */
        opTitle: { en: string; tr: string } | null;
        status: 'applied' | 'failed' | 'rolled_back' | 'pending' | 'rejected' | 'skipped' | 'done';
        platform: string | null;
        target: { level: string; ref: string | null; name: string | null } | null;
        /** Kural adı (otomasyon satırında). */
        rule: string | null;
        /** Operatör koşusunun kısa özeti (AI metni, ≤200) ya da hata KODU. */
        note: string | null;
        /** Geri alma eyleminin türü — eylem kimliği `undo:<id>`. */
        undo: 'rollback' | 'inverse' | null;
        /** `ACTIVITY_NO_UNDO_CODES` — geri alınamazsa neden. */
        noUndo: string | null;
      }[];
      total: number;
    }>
  /**
   * BEKLEYEN ONAYLAR — İNSAN satır içi onaylar/reddeder (AI karar vermez).
   * Karar mevcut uçtan: ajan/operatör kartı `/agent/proposals/:id/decide`,
   * kural önerisi `/automations/approvals/decide`.
   */
  | Actionable<{
      kind: 'approvals';
      items: {
        id: string;
        source: 'agent' | 'automation' | 'operator';
        /** Hedefin adı (kampanya/kural) — platform/kullanıcı metni, düz. */
        title: string;
        /** `cards.activity.action.<kod>` ile aynı sözlük. */
        action: string;
        opTitle: { en: string; tr: string } | null;
        /** Kural önerisinde kuralın adı. */
        rule: string | null;
        platform: string | null;
        createdAt: string;
        expiresAt: string | null;
        decisionRole: WorkspaceRole;
      }[];
      total: number;
    }>
  /** DIŞA AKTARMA — ilerleme + indirme (indirme adresi CANLI, her çizimde taze). */
  | Actionable<{
      kind: 'export';
      exportId: string;
      format: 'csv' | 'pdf' | 'xlsx';
      /** `table` | `summary` | `timeseries` */
      scope: string | null;
      status: 'queued' | 'running' | 'done' | 'failed';
      progress: number | null;
      rows: number | null;
      /** Son bilinen bağlantı süresi — bağlantının kendisi karta yazılmaz. */
      expiresAt: string | null;
    }>
  /**
   * REKLAM HESABI BAKİYELERİ — platformlar arası (`account_balances`): bakiye,
   * harcama limiti, ödeme kaynağının TÜRÜ (kart numarası/son hane YOK). TikTok
   * İşletme Merkezi transferi formu `actions`ta (`topup:<hesap>` /
   * `refund:<hesap>`, `input: 'amount'` — FUNDS, ADMIN, kart içi onay).
   */
  | Actionable<{
      kind: 'balance';
      items: {
        platform: string;
        /** Mentorivo `AdAccount.id`. */
        adAccountId: string;
        accountName: string | null;
        currency: string | null;
        state: BalanceState;
        /** Ön ödemeli bakiye (kalan). Meta'da ödenmemiş fatura `due`da. */
        balance: number | null;
        /** Ödenmemiş tutar (Meta `balance` = biriken fatura). */
        due: number | null;
        spendCap: number | null;
        spent: number | null;
        /** `card` | `invoice` | `prepaid` | `credit_line` | `mixed` | null */
        fundingType: string | null;
        /** `BALANCE_NOTE_CODES` */
        notes: string[];
        /** Platformun resmî faturalama sayfası (https) — yoksa `null`. */
        manageUrl: string | null;
        /** İşletme Merkezi transferi mümkün mü (TikTok). */
        transfer: { available: boolean } | null;
      }[];
      /** Okunamayan platform sayısı — satırda `state: 'error'`. */
      failed: number;
    }>
  /**
   * EYLEM GEREKİYOR — izin/erişim/yeniden bağlanma/özellik eksik/kota/plan.
   * Başarısız katalog işleminde (`platformError.category`, `requires.access`)
   * sunucu doğurur; başarısız ONAY kartında web aynı eşlemeyi
   * (`needsActionFromFailure`) kartın içine gömer. "Yeniden dene" `reply`.
   */
  | Actionable<{
      kind: 'needs_action';
      platform: string | null;
      category: NeedsActionCategory;
      /** Hatanın kodu (`OP_NO_ACCOUNT`, platform kodu…) — yoksa `null`. */
      code: string | null;
      opId: string | null;
      opTitle: { en: string; tr: string } | null;
      /**
       * Platformun terimiyle gereken erişim/kapsam (`requires.access`,
       * `requires.scopes`) — müşteri metni DEĞİL; ekranda "ayrıntı" altında.
       */
      access: string[];
      /** Müşteriye dönük açıklama (platform işleminin müşteri notu) — yoksa `null`. */
      customerNote: string | null;
      /** `cards.needs_action.step.<code>` */
      steps: { code: string; params?: Record<string, string | number> }[];
      /** Kota reddinde platformun söylediği bekleme (sn). */
      retryAfterSeconds: number | null;
    }>
  /**
   * BAĞLANTILAR ÖZETİ — tüm reklam platformu + mağaza durumu. Bağlan/yeniden
   * bağlan `reply` (`connect:<PLATFORM>`) → model bağlantı kartını açar
   * (bağlantı kartı mantığı, OAuth dönüşü sohbete). Demo kaldırma kartın içinde.
   */
  | Actionable<{
      kind: 'connections_overview';
      ads: {
        platform: string;
        state: AgentConnectState;
        account: string | null;
        demo: boolean;
        connectionId: string | null;
      }[];
      stores: {
        platform: string;
        state: AgentConnectState;
        name: string | null;
        authMode: 'oauth' | 'apiKey' | null;
      }[];
      /** Çalışma alanında demo veri var mı ("demoyu kaldır"). */
      demoActive: boolean;
    }>
  /**
   * KALDIRMA ETKİSİ (1 Eki 2026) — bir reklam bağlantısı, tek reklam hesabı,
   * mağaza ya da demo veri kaldırılınca Mentorivo'dan NE GİDER. Okuma aracı
   * `ad_connection_impact`in kartı; aynı gövde kaldırma ONAY kartının içinde
   * (`AgentAppOpDraft.display`) çiziliyor. Ham `records` tablosuna ("Account
   * count", "Metric row count", "—") düşüyordu.
   */
  | Actionable<{ kind: 'disconnect_impact' } & DisconnectImpactView>
  /**
   * SENKRON DURUMU (1 Eki 2026) — `ad_sync_status`: reklam hesabı başına
   * çalışıyor/tamam/başarısız, ilerleme, son senkron, son hata KODU (ham
   * platform metni değil). "Verim güncel mi" sorusunun cevabı.
   */
  | Actionable<{
      kind: 'sync_status';
      accounts: {
        accountId: string;
        /// Reklam hesabının adı (platform metni — veri).
        name: string;
        platform: string | null;
        /** `never` | `running` | `ok` | `failed` (`SyncStatusResponse`). */
        state: string;
        /// 0–100; çalışmıyorsa `null`.
        progress: number | null;
        lastSyncedAt: string | null;
        /// Son hatanın SINIFI (`auth`, `rate_limit`…) ya da `error`; yoksa `null`.
        error: string | null;
      }[];
      initialSyncComplete: boolean;
    }>
  /**
   * HESAP DURUMU — plan, kredi, kullanım, günlük mesaj hakkı, otonomi.
   * SALT OKUNUR: AI değiştiremez; ayar/ödeme/yükseltme bağlantıları `actions`ta
   * (`link`). Görünüm canlı uçtan tazeler (rol izin verdiği kadar).
   */
  | Actionable<{
      kind: 'account_status';
      plan: {
        tier: string | null;
        status: string | null;
        renewsAt: string | null;
        trialDaysRemaining: number | null;
        cancelAtPeriodEnd: boolean;
      } | null;
      credits: { available: number; reserved: number } | null;
      /** `adSpend` | `adAccounts` | `members` | `credits` — `limit: null` sınırsız. */
      usage: { key: string; used: number; limit: number | null }[];
      /** Kişinin BUGÜNKÜ AI mesaj hakkı. */
      messages: { used: number; limit: number; resetsAt: string } | null;
      autonomy: { mode: string; fullAutonomy: boolean; operatorEnabled: boolean } | null;
    }>
  /**
   * EKİP — üyeler (kimlik + rol), davetler; ad/e-posta CANLI. Davet formu
   * insan eylemi (`invite:<ROL>` — `input: 'text'` e-posta, karta YAZILMAZ).
   * Koltuk denetimi kart ÜRETİLİRKEN (`seats`, `inviteBlock`).
   */
  | Actionable<{
      kind: 'team';
      members: { userId: string; role: WorkspaceRole; you: boolean; joinedAt: string | null }[];
      invites: { invitationId: string; role: WorkspaceRole; expiresAt: string | null; status: string }[];
      total: number;
      /** Bekleyen davetler DAHİL; `limit: -1` sınırsız. */
      seats: { used: number; limit: number; remaining: number } | null;
      canInvite: boolean;
      /** Davet neden kapalı: `SEATS_FULL` | `ROLE` | null. */
      inviteBlock: 'SEATS_FULL' | 'ROLE' | null;
    }>
  /**
   * AI'IN HATIRLADIKLARI — ortak hafıza notları (`list_notes`, `remember`).
   * Notun METNİ canlı uçtan (kullanıcı/AI serbest metni; kayda girmez).
   * Silme (arşivleme) insanın düğmesi — REST `/operator/notes/:id` (ADMIN).
   */
  | Actionable<{
      kind: 'memory';
      items: {
        noteId: string;
        /** `FACT` | `LEARNING` | `PREFERENCE` | `HYPOTHESIS` | `TODO` | `WARNING` */
        noteKind: string;
        importance: number;
        pinned: boolean;
        createdBy: 'agent' | 'user';
        platform: string | null;
        entityLevel: string | null;
        expiresAt: string | null;
        updatedAt: string;
      }[];
      total: number;
      /** Bu kartta az önce kaydedilen not (`remember`) — yoksa `null`. */
      savedId: string | null;
      /**
       * BELLEK DOSYALARI (Faz 9 X2) — 1–5 Eki 2026 arasında bellek aracının
       * yazdığı kartlarda kalan alan. Artık üretilmiyor; web bu kartları
       * çizmiyor (`hasCardRenderer`). Yalnız kayıtlı eski satırlar için tipte.
       */
      files?: MemoryCardFile[];
    }>;

/**
 * Kaldırma etkisinin gövdesi — `disconnect_impact` kartı ve kaldırma onay
 * kartının özel gövdesi (`AgentAppOpView`). Sayılar sunucuda; boş liste ve
 * `null` ekranda "yok" diye okunur ("—" değil). 🔴 Kişisel veri YOK: hesap,
 * mağaza ve kural adları iş/platform metni (veri, talimat değil).
 */
export interface DisconnectImpactView {
  /** Neyin kaldırıldığı: tüm reklam bağlantısı · tek reklam hesabı · mağaza · demo veri. */
  target: 'connection' | 'account' | 'store' | 'demo';
  /// `AdPlatform` ya da `StorePlatform`; demo/çok platformda `null`.
  platform: string | null;
  /// Kaldırılan bağlantının / hesabın / mağazanın görünen adı.
  name: string | null;
  /// Giden reklam hesaplarının adları (en çok 10; tamamı `accountCount`).
  accounts: string[];
  accountCount: number;
  /// Mentorivo'daki kampanya KAYITLARI (platformdaki kampanyalar kalır).
  campaignCount: number;
  metricRowCount: number;
  /// Bu hesaplardaki reklamlarda kullanılan Mentorivo kreatifi sayısı.
  publishedCreativeCount: number;
  oldestMetricDate: string | null;
  /// Etkilenebilecek açık kurallar (en çok 10 ad; tamamı `workflowCount`).
  workflows: string[];
  workflowCount: number;
  /// Mağaza: giden ürün / sipariş kopyası; reklam tarafında `null`.
  productCount: number | null;
  orderCount: number | null;
  /// Bağlantıda platformdaki erişim de iptal edilecek mi; diğerlerinde `null`.
  revokeOnPlatform: boolean | null;
}

export const OPS_CARD_KINDS = [
  'disconnect_impact',
  'sync_status',
  'automation_rule',
  'tasks',
  'activity',
  'approvals',
  'export',
  'balance',
  'needs_action',
  'connections_overview',
  'account_status',
  'team',
  'memory',
] as const;

/**
 * Araç adı → kart türü. Ekran araçları (`app-tools/*.tools.ts` `card`),
 * operatör araçları (`agent-operator-tool.service.ts` `ui`) ve kataloğun
 * başarısız işlemi (`needs_action` — araç adından bağımsız, sunucu doğurur).
 */
export const OPS_TOOL_CARDS: Readonly<Record<string, readonly AgentCardKind[]>> = {
  automation_get: ['automation_rule'],
  automation_logs: ['activity'],
  /* 1 Eki 2026 — tek iş kaydı ve tek koşu da zaman çizelgesinde. */
  automation_log_get: ['activity'],
  automation_run_get: ['activity'],
  activity_feed: ['activity'],
  automation_approvals: ['approvals'],
  report_export_start: ['export'],
  report_export_status: ['export'],
  account_balances: ['balance'],
  ad_connections: ['connections_overview'],
  store_connections: ['connections_overview'],
  /* Çekirdek okuma (1 Eki 2026) — ham `records` yerine bağlantılar özeti;
     gösterim varsayılanı GİZLİ (`presentation.ts` araç istisnası). */
  get_connections: ['connections_overview'],
  get_credit_balance: ['account_status'],
  /* 1 Eki 2026 — kaldırma etkisi ve senkron durumu kendi kartında. */
  ad_connection_impact: ['disconnect_impact'],
  ad_sync_status: ['sync_status'],
  billing_subscription: ['account_status'],
  billing_usage: ['account_status'],
  workspace_context: ['account_status'],
  team_list: ['team'],
  list_tasks: ['tasks'],
  schedule_task: ['tasks'],
  list_notes: ['memory'],
  remember: ['memory'],
  /* Bellek aracı (`memory_20250818`) KART ÇİZMİYOR (5 Eki 2026, kullanıcı
     kararı) — yazması etkinlik panelinde "Hafıza güncellendi" adımı. */
};

export const OPS_OP_CARDS: readonly OpCardRoute<AgentCardKind>[] = [];

/** Canlı veri şekilleri — KAYDEDİLMEZ; kişisel veri ve süreli adres yalnız burada. */
export interface OpsCardLiveData {
  team: {
    members: { userId: string; name: string | null; email: string | null }[];
    invites: { invitationId: string; email: string | null }[];
  };
  export: {
    status: 'queued' | 'running' | 'done' | 'failed';
    progress: number | null;
    /** İmzalı, süreli — yalnız bellekte; karta/sohbete yazılmaz. */
    downloadUrl: string | null;
    expiresAt: string | null;
    error: string | null;
  };
  memory: {
    items: { noteId: string; content: string | null; archived: boolean }[];
    /** Bellek dosyalarının metni (Faz 9 X2) — silinmişse `deleted`; kişisel dosya yalnız sahibine. */
    files?: { fileId: string; content: string | null; deleted: boolean }[];
  };
  approvals: { items: { id: string; status: string }[] };
  tasks: {
    operatorEnabled: boolean;
    items: { taskId: string; status: string; dueAt: string | null; lastRunAt: string | null }[];
  };
  /** Kural kartının "deneme çalıştır"ı + son koşular — hiçbir şey kaydedilmez. */
  automation_rule: {
    enabled: boolean | null;
    dryRun: NonNullable<Extract<OpsAgentCard, { kind: 'automation_rule' }>['dryRun']> | null;
    lastRuns: { runId: string; status: string; at: string; applied: number; failed: number }[];
  };
  connections_overview: {
    ads: Extract<OpsAgentCard, { kind: 'connections_overview' }>['ads'];
    stores: Extract<OpsAgentCard, { kind: 'connections_overview' }>['stores'];
    demoActive: boolean;
  };
  account_status: Omit<Extract<OpsAgentCard, { kind: 'account_status' }>, 'kind' | 'actions' | 'actionState' | 'eventPending'>;
}

// ═══════════════════════════════════════════════════════════════════════════
// Başarısızlık → "ne gerekiyor" — sunucu (katalog aracı) ve web (onay kartı)
// ═══════════════════════════════════════════════════════════════════════════

export interface NeedsActionInput {
  /** Hata kodu (`OP_NO_ACCOUNT`, `PLATFORM_REJECTED`, `WRITE_TOOLS_DISABLED`…). */
  code: string | null;
  /** `platformRejection` sınıfı (`details.category`) — yoksa `null`. */
  category: string | null;
  providerCode?: string | null;
  retryAfterSeconds?: number | null;
  platform?: string | null;
}

/** Kullanıcının kendi hesabında rol/izin eksik (jeton sağlam). */
const USER_ROLE_CODES = new Set([
  'ROLE_INSUFFICIENT',
  'PAGE_ROLE_REQUIRED',
  'PERMISSION_DENIED',
  'META_PAGE_NO_MODERATE',
  'META_COMMENT_PERMISSION',
]);
/** Bağlantının izin kapsamı eksik — yeniden bağlanınca verilir. */
const SCOPE_CODES = /(_SCOPE_MISSING|SCOPE_UPGRADE_REQUIRED|MISSING_REPORTING_SCOPE)$/;
/** Platformun Mentorivo uygulamasına vereceği erişim (inceleme, katman). */
const APP_ACCESS_CODES = new Set([
  'GOOGLE_ACCESS_REQUIRED',
  'GOOGLE_PLANNER_ACCESS_REQUIRED',
  'LINKEDIN_ACCESS_REQUIRED',
  'LINKEDIN_PRODUCT_NOT_ENABLED',
  'X_API_V2_NOT_ENROLLED',
  'X_API_V2_UNAUTHORIZED',
  'X_V2_FORBIDDEN',
  'YOUTUBE_API_DISABLED',
  'GOOGLE_DATAMANAGER_DISABLED',
]);

/**
 * Başarısızlığı müşterinin yapacağı adımlara çevirir; eşleme yoksa `null`
 * (kart doğmaz, ekran genel cümleyi basar). SAF — ağ yok.
 *
 * Kurallar: jeton ölü → yeniden bağlan; kapsam eksik → yeniden bağlanıp izin
 * ver; hesapta rol yok → platform yöneticisinden rol iste; UYGULAMA erişimi
 * (inceleme/katman) → müşterinin yapacağı bir şey yok, dürüstçe "Mentorivo
 * tarafında bekliyor" + destek; kota → bekle + tekrar dene; plan → yükselt;
 * Mentorivo rolü → yöneticiye sor.
 */
export function needsActionFromFailure(input: NeedsActionInput): {
  category: NeedsActionCategory;
  steps: { code: NeedsActionStepCode; params?: Record<string, string | number> }[];
  retry: boolean;
} | null {
  const code = input.code ?? '';
  const provider = input.providerCode ?? '';
  const platform = input.platform ?? '';
  const p = platform ? { platform } : undefined;

  if (code === 'OP_NO_ACCOUNT') {
    return { category: 'ACCOUNT_SETUP', steps: [{ code: 'connect_account', ...(p ? { params: p } : {}) }], retry: true };
  }
  if (code === 'OP_DEMO_ACCOUNT') {
    return {
      category: 'ACCOUNT_SETUP',
      steps: [{ code: 'remove_demo' }, { code: 'connect_account', ...(p ? { params: p } : {}) }],
      retry: true,
    };
  }
  if (code === 'WRITE_TOOLS_DISABLED' || code === 'ENTITLEMENT_DENIED' || code === 'PLAN_REQUIRED') {
    return { category: 'PLAN', steps: [{ code: 'upgrade_plan' }], retry: false };
  }
  if (code === 'TOOL_FORBIDDEN' || code === 'CARD_ACTION_FORBIDDEN' || code === 'PROPOSAL_FORBIDDEN' || code === 'FORBIDDEN') {
    return { category: 'PERMISSION', steps: [{ code: 'ask_admin' }], retry: false };
  }
  if (code === 'FEATURE_DISABLED' || code === 'FEATURE_NOT_ENABLED' || /_DISABLED_BY_CONFIG$/.test(code)) {
    return { category: 'FEATURE_FLAG', steps: [{ code: 'feature_not_enabled' }, { code: 'contact_support' }], retry: false };
  }

  const category = input.category ?? (code === 'PLATFORM_AUTH_FAILED' ? 'RECONNECT_REQUIRED' : code === 'RATE_LIMITED' ? 'RATE_LIMITED' : null);
  if (category === 'RECONNECT_REQUIRED') {
    return { category: 'RECONNECT_REQUIRED', steps: [{ code: 'reconnect', ...(p ? { params: p } : {}) }], retry: true };
  }
  if (category === 'RATE_LIMITED') {
    const seconds = typeof input.retryAfterSeconds === 'number' && input.retryAfterSeconds > 0 ? Math.ceil(input.retryAfterSeconds) : null;
    return {
      category: 'QUOTA',
      steps: [{ code: 'wait_and_retry', ...(seconds ? { params: { seconds } } : {}) }],
      retry: true,
    };
  }
  if (category === 'ACCESS_REQUIRED') {
    const key = provider || code;
    if (SCOPE_CODES.test(key)) {
      return { category: 'ACCESS_REQUIRED', steps: [{ code: 'grant_scope', ...(p ? { params: p } : {}) }], retry: true };
    }
    if (USER_ROLE_CODES.has(key)) {
      return {
        category: 'PERMISSION',
        steps: [{ code: 'grant_platform_role', ...(p ? { params: p } : {}) }, { code: 'reconnect', ...(p ? { params: p } : {}) }],
        retry: true,
      };
    }
    if (APP_ACCESS_CODES.has(key)) {
      return {
        category: 'FEATURE_FLAG',
        steps: [{ code: 'wait_platform_access', ...(p ? { params: p } : {}) }, { code: 'contact_support' }],
        retry: false,
      };
    }
    return {
      category: 'ACCESS_REQUIRED',
      steps: [{ code: 'grant_platform_role', ...(p ? { params: p } : {}) }, { code: 'check_platform', ...(p ? { params: p } : {}) }],
      retry: true,
    };
  }
  return null;
}
